import { Component, Input, OnInit } from '@angular/core';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { finalize, take } from 'rxjs';
import { TareaReporte } from 'src/app/features/tasks/tarea-reporte';
import { ModalConfig } from 'src/app/shared/components/modal/modal-config';
import { ReporteData } from '../models/reporte-data';
import { ReportsService } from './../reports.service';
import { DynamicTable, TableHeader } from 'src/app/shared/models/table';

@Component({
  selector: 'app-report-data',
  templateUrl: './report-data.component.html',
  styleUrls: ['./report-data.component.scss']
})
export class ReportDataComponent implements OnInit {

  @Input() reportTask: TareaReporte;
  public modalConfig!: ModalConfig;
  // It contains the filter icon
  faFilter = faFilter;
  // Contains report data to fill the table
  reportData: ReporteData;
  // Report data filtered
  filteredData: any[];
  // Contains records of report data
  tableData: DynamicTable;
  // Contains the quantity of report data records
  maxQuantity: number = 100;
  // Contains the quantity of records that are being showed
  quantity: number = 0;
  // Contains the minimun quatity of records to be showed per page
  minQuantity: number = 10;
  // Contains the actualPage of the table
  actualPage: number = 1;
  // Contains the quaintity of table pages
  pages: number[] = [];
  // Enable or disable table filters
  enableFilters: boolean = false;
  // This property handles the loader state
  isLoading: boolean = false;

  constructor(private reportsService: ReportsService) {
    this.tableData = { headers: [], data: [] }
  }

  ngOnInit() {
    this.modalConfig = {
      modalTitle: this.reportTask.nombre,
      ...this.modalConfig
    }
    this.initReportData();
  }

  /**
  * A function that initializes report data
  * @returns None
  */
  initReportData(): void {
    this.isLoading = true;
    this.reportsService.getReportTaskData(this.reportTask.uuid)
      .pipe(
        take(1),
        finalize(() => this.isLoading = false)).subscribe(response => {
          if (response && response.data.length > 0) {
            this.reportData = response;
            this.filteredData = response.data;
            this.tableData.headers = this.reportData.headers.map((x, i) => ({ key: x, name: x, index: i, sort: "", hasFilters: true } as TableHeader));
            this.setTableData();
            this.setPages(this.reportData.data.length);
            const limit = Math.ceil(this.reportData.data.length / this.minQuantity);
            this.pages = Array.from({ length: limit }, (_, index) => index + 1);
            this.maxQuantity = this.reportData.data.length;
          }
        });
  }

  /**
  * A function that sets the table data
  * @returns None
  */
  setTableData(): void {
    this.tableData.data = this.filteredData.slice(0, this.minQuantity);
    this.quantity = this.tableData.data.length;
  }

  /**
  * A function that changes the page to show the records available in the page
  * @param {number} page - event
  * @returns None
  */
  changePage(page: number): void {
    let pageQuantity = this.minQuantity * page;
    this.tableData.data = this.filteredData.slice(pageQuantity - this.minQuantity, pageQuantity);
    this.actualPage = page;
    this.quantity = pageQuantity - this.minQuantity + this.tableData.data.length;
  }

  /**
  * A function that orders the table data by column
  * @param {TableHeader} header
  * @returns None
  */
  orderData(header: TableHeader): void {
    if (header.sort !== "") {
      this.filteredData.sort((a, b) => {
        if (header.sort === "asc") {
          return a[header.key] > b[header.key] ? 1 : -1
        } else {
          return a[header.key] < b[header.key] ? 1 : -1
        }
      });
    }
    this.setTableData();
    this.changePage(1);
  }

  /**
  * A function that sets the quantity of pages in table data
  * @param {number} length
  * @returns None
  */
  setPages(length: number): void {
    const limit = Math.ceil(length / this.minQuantity);
    this.pages = Array.from({ length: limit }, (_, index) => index + 1);
  }

  /**
   * A function that filters the table data by column
   * @param {TableHeader} header
   * @returns None
   */
  filterByColumn(header: TableHeader): void {
    if (header.value && header.value.trim().length > 0) {
      this.filteredData = this.reportData.data.filter((data) => data[header.key]?.toString().toLowerCase().includes(header.value?.toLowerCase()));
    } else {
      this.filteredData = this.reportData.data;
    }
    this.setPages(this.filteredData.length);
    this.setTableData();
    this.changePage(1);
    this.maxQuantity = this.filteredData.length;
  }

  /**
  * A function that toggles enableFilters property to show or hide filters
  * @returns None
  */
  toggleFilters(): void {
    this.enableFilters = !this.enableFilters;
    if (!this.enableFilters) {
      this.filteredData = this.reportData.data;
      this.setPages(this.filteredData.length);
      this.setTableData();
      this.changePage(1);
      this.maxQuantity = this.filteredData.length;
    }
  }
}